import { isEntry } from "@/lib/contentful/guards";
import type { TypeButton, TypeExternalButton } from "@/types/contentful/generated";
import { stripZeroWidth } from "@/utils/ContentfulHelper";

type ButtonFields = TypeButton<undefined, string>["fields"];
type ExternalButtonFields = TypeExternalButton<undefined, string>["fields"];

export type CtaButtonEntry =
  | {
      sys: { id: string; contentType: { sys: { id: "button" } } };
      fields: ButtonFields;
    }
  | {
      sys: { id: string; contentType: { sys: { id: "externalButton" } } };
      fields: ExternalButtonFields;
    };
export type CtaExperiences = CtaButtonEntry["fields"]["nt_experiences"];

/**
 * A CTA's destination, or `null` when it has none — an unpublished linked page, a page
 * with no slug, or a blank external URL. CTAButton renders nothing in that case, so
 * callers deciding whether a button is worth showing must check this first.
 *
 * Lives outside CTAButton.tsx because that module is `"use client"`: every export of a
 * client module becomes a client reference, and calling one from a server component
 * throws. Server-side callers need this function to be plain.
 */
export function resolveCtaHref(entry: CtaButtonEntry): string | null {
  if (entry.sys.contentType.sys.id === "externalButton") {
    const fields = entry.fields as ExternalButtonFields;
    if (!fields.externalUrl) return null;
    return stripZeroWidth(fields.externalUrl).trim() || null;
  }

  const fields = entry.fields as ButtonFields;
  const page = fields.page;
  if (!isEntry(page)) return null;
  if (!page.fields.slug) return null;
  return `/${stripZeroWidth(page.fields.slug).trim()}${formatAnchor(fields.anchorTag)}`;
}

function formatAnchor(anchorTag?: string): string {
  if (!anchorTag) return "";
  return anchorTag.startsWith("#") ? anchorTag : `#${anchorTag}`;
}
